import React, { Component } from 'react';
import { Form, Button, Select, Input, notification, Card, Typography, Spin, Alert } from 'antd';
import { getProjectList, convertProjectType } from '../../util/APIUtils';
import { inject, observer } from 'mobx-react';

const { Option } = Select;
const { Title } = Typography;

const ALLOWED_CONVERSIONS = { NZ: 'SA-SB', 'SA-SB': 'NZ', VA: 'SA-Si', 'SA-Si': 'VA' };

@inject('projectsStore')
@observer
class ProjectTypeConverter extends Component {
    constructor(props) {
        super(props);
        this.state = {
            projects: [],
            sourceProject: null,
            newDescription: '',
            isLoading: true,
            isSubmitting: false,
            error: null,
        };
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    componentDidMount() {
        this.loadInitialData();
    }

    loadInitialData() {
        this.setState({ isLoading: true });
        getProjectList()
            .then(projectsResponse => {
                this.setState({
                    projects: projectsResponse.filter(p => ALLOWED_CONVERSIONS[p.projectType] !== undefined),
                    isLoading: false,
                });
            })
            .catch(err => {
                this.setState({
                    error: 'Failed to load initial data. Please try again.',
                    isLoading: false,
                });
                notification.error({ message: 'Error', description: 'Could not fetch projects.' });
            });
    }

    handleSourceChange(projectNo) {
        const project = this.state.projects.find(p => p.projectNo === projectNo);
        this.setState({
            sourceProject: project,
            newDescription: project ? (project.description || '') : '',
        });
    }

    handleSubmit(event) {
        const { sourceProject, newDescription } = this.state;

        if (!sourceProject) {
            notification.warning({ message: 'Validation Error', description: 'Please select a source project.' });
            return;
        }

        this.setState({ isSubmitting: true, error: null });

        const conversionRequest = {
            sourceProjectNumber: sourceProject.projectNo,
            targetProjectType: ALLOWED_CONVERSIONS[sourceProject.projectType],
            newDescription: newDescription,
        };

        convertProjectType(conversionRequest)
            .then(response => {
                notification.success({
                    message: 'Conversion Successful!',
                    description: `Project ${sourceProject.projectNo} converted. New number: ${response.targetProjectNumber}.`,
                });

                if (this.props.projectsStore) {
                    this.props.projectsStore.setProjectsAllLoaded(false);
                }

                this.setState({
                    sourceProject: null,
                    newDescription: '',
                });
                this.loadInitialData();
            })
            .catch(err => {
                this.setState({
                    error: err.message || 'The conversion failed. Please check the server logs.',
                });
                notification.error({ message: 'Conversion Failed', description: err.message || 'An unknown error occurred.' });
            })
            .finally(() => {
                this.setState({ isSubmitting: false });
            });
    }

    render() {
        const { isLoading, isSubmitting, error, projects, sourceProject, newDescription } = this.state;

        if (isLoading) {
            return <Spin tip="Loading data..." />;
        }

        if (error && !isSubmitting) {
            return <Alert message="Error" description={error} type="error" showIcon />;
        }

        return (
            <Card title={<Title level={4}>Convert Project Type</Title>} style={{ maxWidth: 600, margin: '24px auto' }}>
                <Form layout="vertical" onFinish={this.handleSubmit}>
                    <Form.Item label="Source Project" required>
                        <Select
                            showSearch
                            placeholder="Select a source project"
                            value={sourceProject ? sourceProject.projectNo : undefined}
                            onChange={value => this.handleSourceChange(value)}
                            filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }
                            optionFilterProp="label"
                        >
                            {projects.map(p => (
                                <Option key={p.projectNo} value={p.projectNo} label={`${p.projectNo} : ${p.description}`}>
                                    {p.projectNo} : {p.description}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {sourceProject && (
                        <>
                            <Form.Item label="Conversion">
                                <span style={{ fontWeight: 500 }}>
                                    {sourceProject.projectType} → {ALLOWED_CONVERSIONS[sourceProject.projectType]}
                                </span>
                            </Form.Item>

                            <Form.Item label="New Title">
                                <Input
                                    value={newDescription}
                                    onChange={e => this.setState({ newDescription: e.target.value })}
                                />
                            </Form.Item>
                        </>
                    )}

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={isSubmitting} disabled={!sourceProject}>
                            Convert Project
                        </Button>
                    </Form.Item>

                    {error && isSubmitting && <Alert message="Conversion Error" description={error} type="error" showIcon />}
                </Form>
            </Card>
        );
    }
}

export default ProjectTypeConverter;
